import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { pinVendorInList } from '../NewPurchaseOrder/apiUtils';
import { CostInput } from '../NewPurchaseOrder/components/create/CurrencyInput';
import SupplierSelectDropdown from '../NewPurchaseOrder/components/create/SupplierSelectDropdown';
import { formatCurrency } from '../../helperFunctions/formatCurrency';
import { useNewPurchaseOrderVendorsQuery } from '../../queries/purchaseOrders/useNewPurchaseOrderVendorsQuery';
import {
  fetchChatbotPoProductById,
  lookupChatbotPoProductByUpc,
  searchChatbotPoProducts,
} from './chatbotPoCatalogApi';
import ChatbotCreateProductFromInvoice from './ChatbotCreateProductFromInvoice';
import ChatbotPoDateField from './ChatbotPoDateField';
import {
  DUPLICATE_UPC_MESSAGE,
  findDuplicateUpcRows,
  formatCreatePoCostPerItem,
  lineTotalAmount,
  looksLikeUpc,
  poCreateAfterQtyDisplay,
  poLineProductVariantSelectionKey,
  resolveCreatePoLineFromProductById,
  tryPurchaseOrderQtyInput,
} from './poCreateLogic';
import { lineTotal } from './purchaseOrderPdfApi';
import styles from './AdminChatbot.module.scss';
import { withReactQueryBoundary } from '../../lib/react-query';

const PRODUCT_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 320,
      fontSize: 12.5,
      fontWeight: 500,
    },
  },
};

const PurchaseOrderPdfReview = ({
  review,
  fileName,
  onChange,
  onRemoveLine,
  onSaveDraft,
  onCreate,
  onCancel,
  busy,
  auth,
  merchantId,
  errorMessage,
}) => {
  const [vendorSearch, setVendorSearch] = useState('');
  const [productQueries, setProductQueries] = useState({});
  const [productOptions, setProductOptions] = useState({});
  const [productLoading, setProductLoading] = useState({});
  const [activeSearchLine, setActiveSearchLine] = useState(null);
  const [createProductLineKey, setCreateProductLineKey] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const searchTimers = useRef({});

  const selectedVendor = useMemo(() => {
    if (!review.vendorId && !review.vendorName) return null;
    return {
      id: review.vendorId || '',
      name: review.vendorName || '',
      email: review.vendorEmail || '',
    };
  }, [review.vendorEmail, review.vendorId, review.vendorName]);

  const vendorsQuery = useNewPurchaseOrderVendorsQuery({
    merchantId,
    auth,
    search: vendorSearch,
    ensureVendorId: review.vendorId || '',
    enabled: Boolean(merchantId && auth?.token),
  });

  const vendorsForDropdown = useMemo(() => {
    let list = vendorsQuery.vendors ?? vendorsQuery.data ?? [];
    (review.vendorCandidates || []).forEach((candidate) => {
      list = pinVendorInList(list, candidate);
    });
    return pinVendorInList(list, selectedVendor);
  }, [
    review.vendorCandidates,
    selectedVendor,
    vendorsQuery.data,
    vendorsQuery.vendors,
  ]);

  const handleVendorSearchChange = useCallback((query) => {
    setVendorSearch(query);
  }, []);

  const handleVendorPick = useCallback(
    (vendorId) => {
      if (!vendorId) {
        onChange((current) =>
          current
            ? {
              ...current,
              vendorId: '',
              vendorName: '',
              vendorEmail: '',
            }
            : current,
        );
        return;
      }
      const picked = vendorsForDropdown.find(
        (vendor) => String(vendor.id) === String(vendorId),
      );
      if (!picked?.id) return;
      onChange((current) =>
        current
          ? {
            ...current,
            vendorId: picked.id,
            vendorName: picked.name,
            vendorEmail: picked.email || current.vendorEmail || '',
          }
          : current,
      );
    },
    [onChange, vendorsForDropdown],
  );

  useEffect(() => {
    const dupRows = findDuplicateUpcRows(review.lines);
    setRowErrors((prev) => {
      const next = { ...prev };
      let changed = false;
      dupRows.forEach((index) => {
        const key = review.lines[index]?.key;
        if (key && next[key] !== DUPLICATE_UPC_MESSAGE) {
          next[key] = DUPLICATE_UPC_MESSAGE;
          changed = true;
        }
      });
      Object.keys(next).forEach((key) => {
        if (next[key] !== DUPLICATE_UPC_MESSAGE) return;
        const index = review.lines.findIndex((line) => line.key === key);
        if (index < 0 || !dupRows.has(index)) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [review.lines]);

  const patchLine = (lineKey, patch) => {
    // Functional update so async enrich (product-by-id) cannot wipe a prior select
    // with a stale `review` snapshot.
    onChange((current) => {
      if (!current) return current;
      return {
        ...current,
        lines: current.lines.map((line) =>
          line.key === lineKey ? { ...line, ...patch } : line,
        ),
      };
    });
  };

  const usedSelectionKeys = new Set(
    review.lines
      .map((line) => poLineProductVariantSelectionKey(line))
      .filter(Boolean),
  );

  const runProductSearch = (lineKey, query) => {
    setProductQueries((current) => ({ ...current, [lineKey]: query }));
    setActiveSearchLine(lineKey);
    const trimmed = query.trim();
    window.clearTimeout(searchTimers.current[lineKey]);
    if (!merchantId) {
      setProductLoading((current) => ({ ...current, [lineKey]: false }));
      setProductOptions((current) => ({ ...current, [lineKey]: [] }));
      return;
    }
    // 1 character: wait for a second char (browse only when empty or 2+).
    if (trimmed.length === 1) {
      setProductLoading((current) => ({ ...current, [lineKey]: false }));
      return;
    }
    setProductLoading((current) => ({ ...current, [lineKey]: true }));
    const delayMs = trimmed.length === 0 ? 0 : 300;
    searchTimers.current[lineKey] = window.setTimeout(async () => {
      try {
        let options = [];
        if (trimmed.length >= 2 && looksLikeUpc(trimmed)) {
          const upcHit = await lookupChatbotPoProductByUpc(auth, {
            merchantId,
            upc: trimmed,
          });
          if (upcHit) options = [upcHit];
        }
        if (options.length === 0) {
          options = await searchChatbotPoProducts(auth, {
            merchantId,
            vendorId: review.vendorId,
            query: trimmed,
          });
        }
        const currentKey = poLineProductVariantSelectionKey(
          review.lines.find((line) => line.key === lineKey) || {},
        );
        const filtered = options.filter((option) => {
          const key = poLineProductVariantSelectionKey({
            productId: option.productId,
            variantId: option.variantId,
            upc: option.upc,
          });
          return !usedSelectionKeys.has(key) || key === currentKey;
        });
        setProductOptions((current) => ({ ...current, [lineKey]: filtered }));
      } catch {
        setProductOptions((current) => ({ ...current, [lineKey]: [] }));
      } finally {
        setProductLoading((current) => ({ ...current, [lineKey]: false }));
      }
    }, delayMs);
  };

  const bindCatalogProduct = async (lineKey, option) => {
    if (!option?.productId) return;
    const existingCost =
      review.lines.find((line) => line.key === lineKey)?.cost || '0.00';
    const bound = {
      productId: option.productId,
      variantId: option.variantId || '0',
      title: option.title,
      upc: option.upc || '',
      stock: option.stock || 0,
      cost:
        option.cost !== '' && option.cost != null ? option.cost : existingCost,
      status: 'matched',
    };
    patchLine(lineKey, bound);
    setProductQueries((current) => ({ ...current, [lineKey]: '' }));
    setActiveSearchLine(null);
    setProductOptions((current) => ({ ...current, [lineKey]: [] }));

    try {
      const detail = await fetchChatbotPoProductById(auth, {
        merchantId,
        productId: option.productId,
      });
      const resolved = resolveCreatePoLineFromProductById(detail, option);
      patchLine(lineKey, {
        productId: option.productId,
        title: option.title,
        stock: resolved.stock,
        upc: resolved.upc || option.upc || '',
        variantId: resolved.variantId || option.variantId || '0',
        cost:
          resolved.cost && resolved.cost !== '0.00'
            ? resolved.cost
            : bound.cost,
        status: 'matched',
      });
    } catch {
      // List/UPC row already bound — detail enrich is best-effort.
    }
  };

  const clearProduct = (lineKey) => {
    patchLine(lineKey, {
      productId: '',
      variantId: '0',
      title: '',
      upc: '',
      stock: 0,
      status: 'unmatched',
    });
    setProductQueries((current) => ({ ...current, [lineKey]: '' }));
  };

  const itemsTotal = review.lines.reduce((sum, line) => sum + lineTotal(line), 0);
  const unmatchedCount = review.lines.filter((line) => !line.productId).length;
  const displayFileName =
    fileName || review.fileName || review.reference || 'Purchase order PDF';
  const createPrefillLine = review.lines.find(
    (line) => line.key === createProductLineKey,
  );

  return (
    <section className={styles.poReview} aria-label="Purchase order review">
      <div className={styles.poReviewScroll}>
        <div className={styles.poDetails}>
          <div className={styles.poDetailsHead}>
            <div className={styles.poFileChip} title={displayFileName}>
              <span className={styles.poFileIcon} aria-hidden="true">
                PDF
              </span>
              <span className={styles.poFileName}>{displayFileName}</span>
            </div>
            <div className={styles.poSummary} role="status">
              <span>
                <strong>{review.lines.length}</strong>
                {review.lines.length === 1 ? ' item' : ' items'}
              </span>
              <span className={styles.poSummaryDot} aria-hidden="true" />
              <span
                className={
                  unmatchedCount > 0 ? styles.poSummaryWarn : styles.poSummaryOk
                }
              >
                {unmatchedCount > 0
                  ? `${unmatchedCount} to match`
                  : 'Ready'}
              </span>
            </div>
          </div>

          {review.warnings.length > 0 && (
            <ul className={styles.poWarnings}>
              {review.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}

          <div className={styles.poVendorField}>
            <span className={styles.poFieldLabel}>Vendor</span>
            <SupplierSelectDropdown
              vendors={vendorsForDropdown}
              vendorId={review.vendorId}
              selectedVendor={selectedVendor}
              onPick={handleVendorPick}
              disabled={busy}
              onSearchChange={handleVendorSearchChange}
              onLoadMore={vendorsQuery.loadMore}
              hasMore={Boolean(vendorsQuery.hasMore)}
              loadingMore={Boolean(vendorsQuery.isFetchingNextPage)}
              loading={Boolean(
                vendorsQuery.isFetching && !vendorsQuery.isFetchingNextPage,
              )}
            />
          </div>

          <div className={styles.poMetaGrid}>
            <label className={styles.poField}>
              <span>Issue</span>
              <ChatbotPoDateField
                value={review.issueDate}
                onChange={(nextDate) =>
                  onChange({ ...review, issueDate: nextDate })
                }
                disabled={busy}
                aria-label="Issue date"
              />
            </label>
            <label className={styles.poField}>
              <span>Stock due</span>
              <ChatbotPoDateField
                value={review.stockDate}
                onChange={(nextDate) =>
                  onChange({ ...review, stockDate: nextDate })
                }
                disabled={busy}
                minDate={review.issueDate || null}
                allowClear
                aria-label="Stock due date"
              />
            </label>
            <label className={styles.poField}>
              <span>Reference</span>
              <input
                value={review.reference}
                onChange={(event) =>
                  onChange({ ...review, reference: event.target.value })
                }
                disabled={busy}
                placeholder="PO #"
              />
            </label>
          </div>
        </div>

        <div className={styles.poLinesHeader}>
          <h4>Lines</h4>
          <span>{review.lines.length}</span>
        </div>

        <div className={styles.poLines}>
          {review.lines.map((line, index) => {
            const matched = Boolean(line.productId);
            const fromInvoice = Boolean(line.fromInvoice || line.extracted);
            const afterQty = poCreateAfterQtyDisplay(line.stock, line.qty);
            const total = lineTotalAmount(line.qty, line.cost);
            const searchValue = matched
              ? line.title
              : productQueries[line.key] || '';
            const isSearching =
              !matched && Boolean(productLoading[line.key]);
            const lineOptions = productOptions[line.key] || [];
            const queryText = String(productQueries[line.key] || '').trim();
            const showResults =
              activeSearchLine === line.key &&
              !matched &&
              (isSearching ||
                lineOptions.length > 0 ||
                queryText.length === 0 ||
                (fromInvoice && queryText.length >= 2));
            const invoiceText =
              line.extracted?.name ||
              line.extractedName ||
              line.extractedDescription ||
              '';

            return (
              <article
                key={line.key}
                className={`${styles.poLine} ${matched ? styles.poLineOk : styles.poLineAttention
                  }`}
              >
                <div className={styles.poLineMain}>
                  <span className={styles.poLineIndex} aria-hidden="true">
                    {index + 1}
                  </span>
                  <div className={styles.poItemCell}>
                    <div
                      className={`${styles.poCombobox} ${isSearching ? styles.poComboboxSearching : ''
                        }`}
                    >
                      {matched && searchValue ? (
                        <Tooltip
                          title={searchValue}
                          placement="top"
                          enterDelay={350}
                          componentsProps={PRODUCT_TOOLTIP_PROPS}
                        >
                          <span className={styles.poProductTooltipAnchor}>
                            <input
                              value={searchValue}
                              readOnly
                              placeholder="Search product by name or UPC"
                              disabled={busy}
                              aria-label={`Product for line ${index + 1}`}
                              autoComplete="off"
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        <input
                          value={searchValue}
                          onChange={(event) => {
                            runProductSearch(line.key, event.target.value);
                          }}
                          onFocus={() => {
                            setActiveSearchLine(line.key);
                            runProductSearch(
                              line.key,
                              productQueries[line.key] || '',
                            );
                          }}
                          onBlur={() => {
                            window.setTimeout(() => {
                              setActiveSearchLine((current) =>
                                current === line.key ? null : current,
                              );
                            }, 150);
                          }}
                          placeholder="Search product by name or UPC"
                          disabled={busy}
                          aria-label={`Product for line ${index + 1}`}
                          aria-busy={isSearching}
                          autoComplete="off"
                        />
                      )}
                      {isSearching ? (
                        <span
                          className={styles.poSearchSpinner}
                          aria-hidden="true"
                        />
                      ) : null}
                      {matched ? (
                        <button
                          type="button"
                          className={styles.poClearProduct}
                          onClick={() => clearProduct(line.key)}
                          disabled={busy}
                          aria-label="Clear product"
                        >
                          Clear
                        </button>
                      ) : null}
                      {showResults ? (
                        <div className={styles.poSuggestPanel}>
                          <ul className={styles.poSuggestList} role="listbox">
                            {isSearching && lineOptions.length === 0 ? (
                              <li className={styles.poSuggestStatus}>
                                Searching…
                              </li>
                            ) : null}
                            {!isSearching &&
                              lineOptions.length === 0 &&
                              queryText.length !== 1 ? (
                              <li className={styles.poSuggestStatus}>
                                No products found
                              </li>
                            ) : null}
                            {lineOptions.map((option) => (
                              <li
                                key={`${option.productId}:${option.variantId}:${option.upc}`}
                              >
                                <button
                                  type="button"
                                  onMouseDown={(event) =>
                                    event.preventDefault()
                                  }
                                  onClick={() =>
                                    bindCatalogProduct(line.key, option)
                                  }
                                  title={option.title}
                                >
                                  <span title={option.title}>
                                    {option.title}
                                  </span>
                                  {option.upc ? (
                                    <small>UPC {option.upc}</small>
                                  ) : null}
                                </button>
                              </li>
                            ))}
                          </ul>
                          {fromInvoice ? (
                            <button
                              type="button"
                              className={styles.poSuggestCreateFixed}
                              data-chatbot-invoice-cta="true"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => {
                                setActiveSearchLine(null);
                                setCreateProductLineKey(line.key);
                              }}
                              title={
                                invoiceText
                                  ? `Create from invoice ${invoiceText}`
                                  : 'Create from invoice'
                              }
                            >
                              Create from invoice
                              {invoiceText ? ` ${invoiceText}` : ''}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.poRemove}
                    onClick={() => onRemoveLine(line.key)}
                    disabled={busy || review.lines.length <= 1}
                    aria-label={`Remove line ${index + 1}`}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>

                <div className={styles.poMetrics}>
                  <label className={styles.poMetric}>
                    <span>Qty</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={6}
                      value={line.qty}
                      onChange={(event) => {
                        const result = tryPurchaseOrderQtyInput(
                          event.target.value,
                        );
                        if (!result.ok) {
                          setRowErrors((current) => ({
                            ...current,
                            [line.key]: result.message,
                          }));
                          return;
                        }
                        setRowErrors((current) => {
                          if (!current[line.key]) return current;
                          const next = { ...current };
                          delete next[line.key];
                          return next;
                        });
                        patchLine(line.key, { qty: result.value });
                      }}
                      disabled={busy}
                      aria-label={`Qty for line ${index + 1}`}
                    />
                  </label>
                  <div
                    className={styles.poMetricRead}
                    title="On-hand + order qty"
                  >
                    <span>After</span>
                    <strong>{afterQty}</strong>
                  </div>
                  <label className={styles.poMetric}>
                    <span>Cost</span>
                    <CostInput
                      value={Number(line.cost) || 0}
                      onChange={(nextCost) =>
                        patchLine(line.key, {
                          cost: formatCreatePoCostPerItem(nextCost),
                        })
                      }
                      disabled={busy}
                      width="100%"
                      compact
                      inputProps={{
                        'aria-label': `Cost for line ${index + 1}`,
                      }}
                    />
                  </label>
                  <div className={styles.poMetricRead}>
                    <span>Total</span>
                    <strong>{formatCurrency(Number(total) || 0)}</strong>
                  </div>
                </div>

                {!matched && fromInvoice ? (
                  <button
                    type="button"
                    className={styles.poInvoiceBar}
                    data-chatbot-invoice-cta="true"
                    onClick={() => setCreateProductLineKey(line.key)}
                    disabled={busy}
                    title={
                      invoiceText
                        ? `Create from invoice ${invoiceText}`
                        : 'Create from invoice'
                    }
                  >
                    <span className={styles.poInvoiceBarLabel}>
                      Create from invoice
                      {invoiceText ? ` ${invoiceText}` : ''}
                    </span>
                  </button>
                ) : null}

                {matched && line.upc ? (
                  <p className={styles.poMetaLine}>UPC {line.upc}</p>
                ) : null}

                {rowErrors[line.key] ? (
                  <p className={styles.poRowError}>{rowErrors[line.key]}</p>
                ) : null}
              </article>
            );
          })}
        </div>
      </div>

      <footer className={styles.poStickyBar}>
        {errorMessage ? (
          <p className={styles.poFormError} role="alert">
            {errorMessage}
          </p>
        ) : null}
        <div className={styles.poTotals}>
          <span className={styles.poTotalsLabel}>Total</span>
          <strong className={styles.poTotalsValue}>
            {formatCurrency(Number(itemsTotal) || 0)}
          </strong>
          {review.invoiceTotal != null && (
            <span className={styles.poInvoiceHint}>
              Invoice {review.invoiceTotal}
            </span>
          )}
        </div>
        <div className={styles.poActions}>
          <button
            type="button"
            className={styles.poSecondary}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.poSecondary}
            onClick={onSaveDraft}
            disabled={busy}
          >
            Draft
          </button>
          <button
            type="button"
            className={styles.poPrimary}
            onClick={onCreate}
            disabled={busy}
          >
            Create
          </button>
        </div>
      </footer>

      <ChatbotCreateProductFromInvoice
        open={Boolean(createProductLineKey)}
        auth={auth}
        merchantId={merchantId}
        busy={busy}
        prefill={{
          // Same seeding as AI CreatePurchaseOrderPanel.openAddProductForRow
          name:
            createPrefillLine?.extracted?.name ||
            createPrefillLine?.extractedName ||
            createPrefillLine?.extractedDescription ||
            String(productQueries[createPrefillLine?.key] || '').trim() ||
            '',
          upc:
            createPrefillLine?.extracted?.upc ||
            createPrefillLine?.extractedUpc ||
            '',
          cost:
            createPrefillLine?.cost ||
            createPrefillLine?.extracted?.unit_price ||
            '',
          quantity:
            createPrefillLine?.extracted?.quantity ??
            createPrefillLine?.qty ??
            null,
          note: createPrefillLine?.extracted?.item_code
            ? `Extracted from invoice: ${createPrefillLine.extracted.item_code}`
            : 'Extracted from invoice — review before saving.',
        }}
        onClose={() => setCreateProductLineKey(null)}
        onCreated={(created) => {
          if (!createProductLineKey) return;
          bindCatalogProduct(createProductLineKey, {
            productId: created.productId,
            variantId: created.variantId || '0',
            title: created.title,
            upc: created.upc,
            stock: created.stock || 0,
            cost: created.cost,
          });
          setCreateProductLineKey(null);
        }}
      />
    </section>
  );
};

export default withReactQueryBoundary(PurchaseOrderPdfReview);
